import React from "react";
import {
  Select,
  Label,
  Input,
  Text,
  makeStyles,
} from "@fluentui/react-components";
import { CoverageComponentProps } from "./types";

const useStyles = makeStyles({
  rightAlignedInput: {
    "& input": {
      textAlign: "right",
    },
  },
  section: {
    marginBottom: "24px",
  },
});

const CargoCoverage: React.FC<CoverageComponentProps<"cargo">> = ({
  config,
  onConfigChange,
  t,
  classes,
}) => {
  const styles = useStyles();

  // Format number with thousands separators
  const formatNumber = (num: number | undefined): string => {
    if (num === undefined || num === null || num === 0) return "";
    return num.toLocaleString("cs-CZ");
  };

  // Parse formatted string back to number
  const parseFormattedNumber = (value: string): number => {
    const cleaned = value.replace(/\s/g, "").replace(/,/g, "");
    return parseInt(cleaned) || 0;
  };

  return (
    <>
      {/* Cargo Section */}
      <div className={styles.section}>
        <Label
          style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}
        >
          Cargo
        </Label>

        <div
          style={{
            display: "flex",
            gap: "16px",
            alignItems: "flex-end",
            justifyContent: "flex-start",
            marginBottom: "16px",
          }}
        >
          <div
            className={classes.fieldGroup}
            style={{ flex: "0 0 calc(33.33% - 8px)" }}
          >
            <Label htmlFor="cargoType" style={{ marginBottom: "4px" }}>
              Coverage type
            </Label>
            <Select
              id="cargoType"
              value={config.cargoType || "notSelected"}
              onChange={(_, data) =>
                onConfigChange({
                  cargoType: data.value,
                  // Reset dependent fields when changing to notSelected
                  ...(data.value === "notSelected"
                    ? {
                        cargoLimit: 0,
                        cargoRiskGroup: "1",
                      }
                    : {}),
                })
              }
            >
              <option value="notSelected">Not selected</option>
              <option value="withoutTheft">Without theft</option>
              <option value="withTheft">With theft</option>
            </Select>
          </div>

          {config.cargoType !== "notSelected" && (
            <>
              <div
                className={classes.fieldGroup}
                style={{ flex: "0 0 calc(33.33% - 8px)" }}
              >
                <Label
                  htmlFor="cargoLimit"
                  required
                  style={{ marginBottom: "4px" }}
                >
                  Coverage limit
                </Label>
                <Input
                  id="cargoLimit"
                  className={styles.rightAlignedInput}
                  value={formatNumber(config.cargoLimit)}
                  onChange={(_, data) => {
                    const numValue = parseFormattedNumber(data.value);
                    // Limit the value between 20,000 and 1,000,000
                    const limitedValue = Math.min(
                      1000000,
                      Math.max(20000, numValue || 20000)
                    );
                    onConfigChange({
                      cargoLimit: limitedValue,
                    });
                  }}
                  contentAfter={<Text>Kč</Text>}
                  placeholder="20,000 - 1,000,000"
                />
              </div>

              <div
                className={classes.fieldGroup}
                style={{ flex: "0 0 calc(33.33% - 8px)" }}
              >
                <Label
                  htmlFor="cargoRiskGroup"
                  required
                  style={{ marginBottom: "4px" }}
                >
                  Risk group
                </Label>
                <Select
                  id="cargoRiskGroup"
                  value={config.cargoRiskGroup || "1"}
                  onChange={(_, data) =>
                    onConfigChange({
                      cargoRiskGroup: data.value,
                    })
                  }
                >
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                </Select>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Luggage Section */}
      <div className={styles.section}>
        <Label
          style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}
        >
          Luggage
        </Label>

        <div
          style={{
            display: "flex",
            gap: "16px",
            alignItems: "flex-end",
            justifyContent: "flex-start",
          }}
        >
          <div
            className={classes.fieldGroup}
            style={{ flex: "0 0 calc(33.33% - 8px)" }}
          >
            <Label htmlFor="luggageType" style={{ marginBottom: "4px" }}>
              Coverage type
            </Label>
            <Select
              id="luggageType"
              value={config.luggageType || "notSelected"}
              onChange={(_, data) =>
                onConfigChange({
                  luggageType: data.value,
                  // Reset dependent field when changing to notSelected
                  ...(data.value === "notSelected" ? { luggageLimit: 0 } : {}),
                })
              }
            >
              <option value="notSelected">Not selected</option>
              <option value="withoutTheft">Without theft</option>
              <option value="withTheft">With theft</option>
            </Select>
          </div>

          {config.luggageType !== "notSelected" && (
            <div
              className={classes.fieldGroup}
              style={{ flex: "0 0 calc(33.33% - 8px)" }}
            >
              <Label
                htmlFor="luggageLimit"
                required
                style={{ marginBottom: "4px" }}
              >
                Coverage limit
              </Label>
              <Input
                id="luggageLimit"
                className={styles.rightAlignedInput}
                value={formatNumber(config.luggageLimit)}
                onChange={(_, data) => {
                  const numValue = parseFormattedNumber(data.value);
                  // Limit the value between 5,000 and 500,000
                  const limitedValue = Math.min(
                    500000,
                    Math.max(5000, numValue || 5000)
                  );
                  onConfigChange({
                    luggageLimit: limitedValue,
                  });
                }}
                contentAfter={<Text>Kč</Text>}
                placeholder="5,000 - 500,000"
              />
            </div>
          )}

          <div style={{ flex: "0 0 calc(33.33% - 8px)" }}></div>
        </div>
      </div>
    </>
  );
};

export default CargoCoverage;
